import React, { useEffect, useState } from "react";
import { Row, Col, Button, Image, Badge } from "react-bootstrap";
import { getUserProfiles } from "Utils/user";
import { notifyError, notifySuccess, ToastBox } from "MyComponents/Toaster";
import { SpinnerBox } from "./Spinner";
import config from "../config";
import { useParams } from "react-router-dom";
import ConfirmationModal from "MyComponents/ConfirmationModal";
import axios from "axios";

// Reusable LinkedProfile card
interface LinkedProfileProps {
    profileid: string;
    name: string;
    linkedto: string;
    linkedtocontact: string;
    email: string;
    avatar: string;
}

const LinkedProfile: React.FC<LinkedProfileProps> = ({ profileid, name, linkedto, linkedtocontact, email, avatar }) => (
    <div className="d-flex align-items-center bg-white rounded-4 p-3 shadow-sm mb-3">
        <Image src={avatar+name} roundedCircle width={50} height={50} className="me-3" />
        <div className="flex-grow-1">
            <div className="fw-semibold">{name}</div>
            <div className="text-muted small">Profile ID: {profileid}</div>
            <div className="text-muted small">Linked to: {linkedto}</div>
            <div className="text-muted small">Entity Contact:{linkedtocontact}</div>
        </div>
        <Button variant="outline-secondary" size="sm">
            Switch
        </Button>
    </div>
);

function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}


// Main User Profiles section
const UserProfilesSection: React.FC = () => {

    const { userid } = useParams();
    const [loading, setLoading] = useState(false);
    const startLoading = () => setLoading(true);
    const stopLoading = () => setLoading(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
    const [profiles, setProfiles] = useState<any[]>([]);

    const handleUnlink = async (profileId: number) => {
        try {
            startLoading();
            await axios.post(`${config.NODE_API_BASE_URL}/wso2/unlinkProfile`, {
            profileId
            });
            notifySuccess("Profile unlinked successfully.");
            // Re-fetch profiles
            const data = await getUserProfiles(userid);
            setProfiles(data);
        } catch (error) {
            console.error("Unlink failed:", error);
            // notifyError("Failed to unlink profile.");
        } finally {
            stopLoading();
        }
    };

    useEffect(() => {
        const fetchUserProfiles = async () => {
            try {
                startLoading();
                const response = await getUserProfiles(userid);
                console.log("response", response);
                setProfiles(response);
                stopLoading();
            } catch (err) {
                console.log("error", err);
                notifyError("Failed to load user data");
                stopLoading();
            }
        };

        fetchUserProfiles();
    }, [userid]);

    return (

        <>
            <ConfirmationModal
                show={showConfirmModal}
                title="Confirm Unlink"
                message="Are you sure you want to unlink this profile?"
                onCancel={() => setShowConfirmModal(false)}
                onConfirm={async () => {
                    if (selectedProfileId !== null) {
                        await handleUnlink(selectedProfileId);
                        setShowConfirmModal(false);
                    }
                }}
            />
            <div className="mx-auto" style={{ maxWidth: 800 }}>

                {/* User Profile Card */}
                <div className="bg-light rounded-4 shadow-sm p-4 mb-4">
                    {profiles.filter(p =>   p.linked_entity_id && p.linked_entity_data === null).length === 0 ? (
                        <p className="text-muted">No profiles found.</p>
                    ) : (
                        profiles
                            .filter(p => p.linked_entity_id && p.linked_entity_data === null) // ✅ Only profiles where linked_entity_data is null
                            .map((profile, index) => (
                                <div key={index} className="d-flex align-items-center">
                                    <Image
                                        src="https://avatar.iran.liara.run/public"
                                        roundedCircle
                                        width={70}
                                        height={70}
                                        className="me-3"
                                    />
                                    <div className="flex-grow-1">
                                        <div className="fw-bold fs-5 mb-1">{profile.display_name}</div>
                                        <div className="text-muted mb-2">I'm a Product Designer based in Melbourne.</div>
                                        <div className="d-flex flex-wrap align-items-center text-muted small gap-3">
                                            <div>{capitalizeFirstLetter(profile.account_type)}</div>|
                                            <div>{profile.profile_uuid}</div>|
                                            <div>KYC: Verified</div>
                                            <div><a href={`mailto:${profile.email}`} className="text-muted">{profile.email}</a></div>
                                        </div>
                                    </div>
                                </div>
                            ))
                    )}



                </div>

                {/* Linked Profiles */}
                <div>
                    <h6 className="fw-semibold mb-3">Linked Profiles</h6>
                    {profiles.filter(p => p.linked_entity_id && p.linked_entity_data).length === 0 ? (
                        <p className="text-muted">No linked profiles found.</p>
                    ) : (
                        profiles
                            .filter(p => p.linked_entity_id && p.linked_entity_data)
                            .map((profile, index) => (
                                <LinkedProfile
                                    key={index}
                                    profileid={profile.profile_uuid || ""}
                                    name={profile.display_name || "Unnamed User"}
                                    linkedto={profile.linked_entity_data.display_name}
                                    linkedtocontact={profile.linked_entity_data.email}
                                    email={profile.email || ""}
                                    avatar="https://avatar.iran.liara.run/username?username="
                                />
                            ))
                    )}
                </div>
            </div>
        </>
    );
};

export default UserProfilesSection;
